import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from '@app/core/services/auth.service';
import { HTTP_STATUS_CODE } from '@app/core/constants/system.constants';

@Component({
  selector: 'app-confirm-contact',
  templateUrl: './confirm-contact.component.html',
  styleUrls: ['./confirm-contact.component.scss']
})
export class ConfirmContactComponent implements OnInit, OnDestroy {
  bgLoginPath = environment.bgLogin;
  logoPath = environment.logo;
  form!: FormGroup;
  isLoading = false;
  isSubmitted = false;
  otpSent = false;
  otpDuration = 0;
  maskedOtpEmail = '';
  contactEmail = '';
  fullName = '';
  /** Giá trị personal_email đang lưu trên hệ thống (để biết có đổi email không) */
  originalPersonalEmail = '';
  /** BE config contact.confirm.otp-enabled */
  otpEnabled = false;
  resumeAuthorizeUrl: string | null = null;
  private emailSub?: Subscription;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      personalEmail: ['', [Validators.required, Validators.email]],
      mobileNumber: ['', [Validators.required, Validators.pattern(/^\+?[\d\s\-()]{9,20}$/)]],
      otpCode: ['']
    });
    this.emailSub = this.form.get('personalEmail')!.valueChanges.subscribe(() => {
      this.otpSent = false;
      this.form.get('otpCode')?.reset('');
      this.updateOtpValidators();
    });
    this.loadContact();
  }

  ngOnDestroy(): void {
    this.emailSub?.unsubscribe();
  }

  get f() {
    return this.form.controls;
  }

  /** Cần OTP khi bật config và email cá nhân thay đổi */
  get needOtp(): boolean {
    return this.otpEnabled && this.emailChanged;
  }

  /** Cần OTP khi nhập email mới hoặc đổi khác email đang lưu */
  get emailChanged(): boolean {
    const current = (this.form?.get('personalEmail')?.value || '').trim().toLowerCase();
    const original = (this.originalPersonalEmail || '').trim().toLowerCase();
    return current !== original;
  }

  loadContact(): void {
    const cached = sessionStorage.getItem('ssoContactData');
    if (cached) {
      try {
        this.applyContactData(JSON.parse(cached));
        sessionStorage.removeItem('ssoContactData');
        return;
      } catch {
        sessionStorage.removeItem('ssoContactData');
      }
    }

    this.isLoading = true;
    this.authService.getMyContact().subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.code !== HTTP_STATUS_CODE.SUCCESS) {
          this.toastr.error(res?.message || this.translate.instant('common.error.system'));
          this.router.navigate(['/login']).then();
          return;
        }
        this.applyContactData(res.data || {});
      },
      error: (err) => {
        this.isLoading = false;
        this.toastr.error(err?.error?.message || this.translate.instant('common.error.system'));
        this.router.navigate(['/login']).then();
      }
    });
  }

  private applyContactData(data: any): void {
    this.isLoading = false;
    this.contactEmail = data.email || '';
    this.fullName = data.fullName || '';
    this.resumeAuthorizeUrl = data.resumeAuthorizeUrl || null;
    this.originalPersonalEmail = data.personalEmail || '';
    this.otpEnabled = !!data.otpEnabled;
    this.form.patchValue({
      personalEmail: data.personalEmail || '',
      mobileNumber: data.mobileNumber || ''
    }, { emitEvent: false });
    this.otpSent = false;
    this.updateOtpValidators();
  }

  sendOtp(): void {
    this.isSubmitted = true;
    const emailCtrl = this.form.get('personalEmail');
    if (emailCtrl?.invalid) {
      return;
    }
    if (!this.needOtp) {
      this.toastr.info('Hệ thống không yêu cầu OTP khi cập nhật liên hệ');
      return;
    }
    this.isLoading = true;
    this.authService.sendContactOtp(emailCtrl!.value.trim()).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res?.code !== HTTP_STATUS_CODE.SUCCESS) {
          this.toastr.error(res?.message);
          return;
        }
        this.otpSent = true;
        this.otpDuration = res?.data?.duration || 90;
        this.maskedOtpEmail = res?.data?.email || emailCtrl!.value;
        this.updateOtpValidators();
        this.toastr.success('Đã gửi mã OTP tới ' + this.maskedOtpEmail);
      },
      error: (err) => this.handleError(err)
    });
  }

  submitUpdate(): void {
    this.isSubmitted = true;
    this.updateOtpValidators();
    if (this.form.invalid) {
      return;
    }
    if (this.needOtp && !this.otpSent) {
      this.toastr.warning('Vui lòng gửi mã OTP tới email cá nhân mới trước khi lưu');
      return;
    }
    this.isLoading = true;
    const value = this.form.value;
    this.authService.updateContact({
      personalEmail: value.personalEmail.trim(),
      mobileNumber: value.mobileNumber.trim(),
      otpCode: this.needOtp ? (value.otpCode || '').trim() : ''
    }).subscribe({
      next: (res) => this.handleSuccess(res),
      error: (err) => this.handleError(err)
    });
  }

  logout(): void {
    this.isLoading = true;
    sessionStorage.removeItem('ssoState');
    sessionStorage.removeItem('ssoReqParams');
    this.authService.logoutSession().subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['/login']).then();
      },
      error: () => {
        this.isLoading = false;
        this.router.navigate(['/login']).then();
      }
    });
  }

  private updateOtpValidators(): void {
    const otpCtrl = this.form.get('otpCode');
    if (this.needOtp) {
      otpCtrl?.setValidators([Validators.required]);
    } else {
      otpCtrl?.clearValidators();
    }
    otpCtrl?.updateValueAndValidity({ emitEvent: false });
  }

  private handleSuccess(res: any): void {
    if (res?.code === HTTP_STATUS_CODE.SUCCESS) {
      const resume = res?.data?.resumeAuthorizeUrl || this.resumeAuthorizeUrl;
      this.redirectAfterConfirm(resume);
      return;
    }
    this.isLoading = false;
    this.toastr.error(res?.message);
  }

  private handleError(err: any): void {
    this.isLoading = false;
    this.toastr.error(err?.error?.message || this.translate.instant('common.error.system'));
  }

  private redirectAfterConfirm(resumeAuthorizeUrl?: string | null): void {
    const state = sessionStorage.getItem('ssoState') || '';
    const reqParams = sessionStorage.getItem('ssoReqParams') || '';
    if (reqParams) {
      sessionStorage.removeItem('ssoState');
      sessionStorage.removeItem('ssoReqParams');
      window.location.href = `${environment.backend.sso}/oauth2/authorize?state=${state}&${reqParams}`;
      return;
    }
    if (resumeAuthorizeUrl) {
      window.location.href = resumeAuthorizeUrl;
      return;
    }
    this.isLoading = false;
    this.toastr.success('Đã cập nhật thông tin liên hệ');
    this.router.navigate(['/login']).then();
  }
}
